import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

/** True when the string is likely HTML from Outlook / Graph API rather than plain text. */
export function looksLikeHtml(body: string): boolean {
  const t = body.trimStart().slice(0, 4000)
  return (
    /^<!DOCTYPE\s+html/i.test(t) ||
    /<html[\s>]/i.test(t) ||
    /<body[\s>]/i.test(t) ||
    /<div[\s>]/i.test(t) ||
    /<table[\s>]/i.test(t) ||
    /<p[\s>]/i.test(t) ||
    /<span[\s>]/i.test(t)
  )
}

/** Plain-text snippet for inbox list previews. */
export function stripHtmlToText(html: string): string {
  if (!html) return ''
  if (!looksLikeHtml(html)) {
    return html.replace(/\s+/g, ' ').trim()
  }
  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim()
  }
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
}

function sanitizeEmailHtml(html: string): string {
  if (typeof DOMParser === 'undefined') {
    return html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
  }

  const doc = new DOMParser().parseFromString(html, 'text/html')
  doc.querySelectorAll('script').forEach((element) => element.remove())

  let base = doc.querySelector('base')
  if (!base) {
    base = doc.createElement('base')
    const head = doc.head ?? doc.createElement('head')
    if (!doc.head) {
      doc.documentElement.insertBefore(head, doc.body)
    }
    head.insertBefore(base, head.firstChild)
  }
  base.setAttribute('target', '_blank')

  doc.querySelectorAll('a[href]').forEach((anchor) => {
    anchor.setAttribute('target', '_blank')
    anchor.setAttribute('rel', 'noopener noreferrer')
  })

  return doc.documentElement.outerHTML
}

type EmailBodyProps = {
  html: string
  className?: string
  plainTextClassName?: string
}

/**
 * Renders Outlook / Graph HTML in a sandboxed iframe, or plain text in a pre block.
 */
export function EmailBody({ html, className, plainTextClassName }: EmailBodyProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    if (!looksLikeHtml(html)) return

    const iframe = iframeRef.current
    if (!iframe) return

    const doc = iframe.contentDocument ?? iframe.contentWindow?.document
    if (!doc) return

    const resize = () => {
      if (doc.body) {
        iframe.style.height = `${doc.body.scrollHeight}px`
      }
    }

    const sanitized = sanitizeEmailHtml(html)

    doc.open()
    doc.write(sanitized)
    doc.close()

    iframe.onload = resize
    resize()

    const observed = doc.body
    const ro = observed ? new ResizeObserver(resize) : null
    if (observed && ro) ro.observe(observed)

    return () => {
      iframe.onload = null
      ro?.disconnect()
    }
  }, [html])

  if (!looksLikeHtml(html)) {
    return (
      <pre
        className={cn(
          'whitespace-pre-wrap font-sans text-sm leading-relaxed text-gray-700',
          plainTextClassName,
          className,
        )}
      >
        {html}
      </pre>
    )
  }

  return (
    <iframe
      ref={iframeRef}
      sandbox="allow-same-origin allow-popups"
      title="Email body"
      className={cn('block w-full border-0', className)}
    />
  )
}
